import { supabase } from './supabaseClient';
import { getNextAthensYearMonth } from './dashboardUtils';
import type { ArtistProfile, Payment, TattooEntry } from '../types/dashboard';

export async function fetchArtists(): Promise<ArtistProfile[]> {
  const { data, error } = await supabase
    .from('artist_profiles')
    .select('id, display_name, email, role, is_active, artist_rate, payment_cycle_anchor')
    .eq('role', 'artist')
    .order('display_name', { ascending: true });
  if (error) throw error;
  return (data || []) as ArtistProfile[];
}

export async function fetchEntries(params: {
  artistId?: string;
  from?: string;
  to?: string;
  search?: string;
}): Promise<TattooEntry[]> {
  let query = supabase
    .from('tattoo_entries')
    .select('*')
    .order('performed_at', { ascending: false });

  if (params.artistId) query = query.eq('artist_id', params.artistId);
  if (params.from) query = query.gte('performed_date', params.from);
  if (params.to) query = query.lte('performed_date', params.to);
  if (params.search) query = query.ilike('description', `%${params.search}%`);

  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as TattooEntry[];
}

export async function createEntry(input: {
  artistId: string;
  performedAt: string;
  performedDate: string;
  description: string;
  costEur: number;
  paymentMethod: 'cash' | 'card' | 'split';
  cashAmount?: number | null;
  cardAmount?: number | null;
  ownCustomer: boolean;
  workPeriod?: 'inside' | 'outside' | null;
  tipEur?: number | null;
  note?: string;
}): Promise<void> {
  const { error } = await supabase.from('tattoo_entries').insert({
    artist_id: input.artistId,
    performed_at: input.performedAt,
    performed_date: input.performedDate,
    description: input.description,
    cost_eur: input.costEur,
    payment_method: input.paymentMethod,
    cash_amount: input.paymentMethod === 'split' ? (input.cashAmount ?? 0) : null,
    card_amount: input.paymentMethod === 'split' ? (input.cardAmount ?? 0) : null,
    own_customer: input.ownCustomer,
    work_period: input.ownCustomer ? (input.workPeriod || 'inside') : null,
    tip_eur: input.tipEur ?? 0,
    note: input.note || null,
  });
  if (error) throw error;
}

export async function updateEntry(entryId: string, input: {
  performedAt: string;
  performedDate: string;
  description: string;
  costEur: number;
  paymentMethod: 'cash' | 'card' | 'split';
  cashAmount?: number | null;
  cardAmount?: number | null;
  ownCustomer: boolean;
  workPeriod?: 'inside' | 'outside' | null;
  tipEur?: number | null;
  note?: string;
}): Promise<void> {
  const { error } = await supabase.from('tattoo_entries').update({
    performed_at: input.performedAt,
    performed_date: input.performedDate,
    description: input.description,
    cost_eur: input.costEur,
    payment_method: input.paymentMethod,
    cash_amount: input.paymentMethod === 'split' ? (input.cashAmount ?? 0) : null,
    card_amount: input.paymentMethod === 'split' ? (input.cardAmount ?? 0) : null,
    own_customer: input.ownCustomer,
    work_period: input.ownCustomer ? (input.workPeriod || 'inside') : null,
    tip_eur: input.tipEur ?? 0,
    note: input.note || null,
  }).eq('id', entryId);
  if (error) throw error;
}

export async function deleteEntry(entryId: string): Promise<void> {
  const { error } = await supabase.from('tattoo_entries').delete().eq('id', entryId);
  if (error) throw error;
}

export async function fetchPayments(artistId: string): Promise<Payment[]> {
  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('artist_id', artistId)
    .order('paid_at', { ascending: false });
  if (error) throw error;
  return (data || []) as Payment[];
}

export async function recordPayment(params: {
  artistId: string;
  periodStart: string;
  periodEnd: string;
  grossTotal: number;
  payoutTotal: number;
  entryIds: string[];
  notes?: string;
}): Promise<void> {
  const { data: payment, error: paymentError } = await supabase
    .from('payments')
    .insert({
      artist_id: params.artistId,
      period_start: params.periodStart,
      period_end: params.periodEnd,
      gross_total: params.grossTotal,
      payout_total: params.payoutTotal,
      paid_at: new Date().toISOString(),
      notes: params.notes || null,
    })
    .select('id')
    .single();
  if (paymentError) throw paymentError;

  if (params.entryIds.length) {
    const { error: updateError } = await supabase
      .from('tattoo_entries')
      .update({ payment_id: payment.id })
      .in('id', params.entryIds);
    if (updateError) throw updateError;
  }
}

async function getAccessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token ?? null;
}

/**
 * Ensures the admin user has an artist_profiles row with role='admin'.
 * Must be called before any dashboard queries so that RLS is_admin() works.
 */
export async function bootstrapAdmin(): Promise<void> {
  const token = await getAccessToken();
  if (!token) return;
  try {
    await fetch('/.netlify/functions/admin-bootstrap', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${token}`,
      },
    });
  } catch {
    // Silently ignore bootstrap errors
  }
}

export async function createArtistAccount(input: {
  email: string;
  password: string;
  displayName: string;
  artistRate?: number;
}): Promise<{ userId: string }> {
  const token = await getAccessToken();
  const response = await fetch('/.netlify/functions/admin-create-artist', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to create artist');
  }
  return response.json();
}

export async function toggleArtistActive(input: { userId: string; isActive: boolean }): Promise<void> {
  const token = await getAccessToken();
  const response = await fetch('/.netlify/functions/admin-toggle-artist', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to update artist');
  }
}

export async function deleteArtistAccount(input: { userId: string }): Promise<void> {
  const token = await getAccessToken();
  const response = await fetch('/.netlify/functions/admin-delete-artist', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to delete artist');
  }
}

/* ── Monthly fixed expenses (baseline, overrides, legacy adjustments) ─ */

const DEFAULT_MONTHLY_EXPENSES = 4200;
const EXPENSE_ADJUSTMENTS_KEY = 'expense_adjustments';
const EXPENSE_BASELINE_KEY = 'expense_baseline';
const EXPENSE_MONTH_OVERRIDES_KEY = 'expense_month_overrides';

export const getDefaultMonthlyExpenses = () => DEFAULT_MONTHLY_EXPENSES;

/** year-month key e.g. "2025-02" */
export function toYearMonthKey(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

export type ExpenseAdjustmentsMap = Record<string, number>;
export type ExpenseBaselineMap = Record<string, number>;
export type ExpenseMonthOverridesMap = Record<string, number>;

export type ExpenseBudgetState = {
  baseline: ExpenseBaselineMap;
  monthOverrides: ExpenseMonthOverridesMap;
  adjustments: ExpenseAdjustmentsMap;
};

/** For each YYYY-MM, amount applies from that month onward until a later segment. */
export async function fetchExpenseBaselines(): Promise<ExpenseBaselineMap> {
  const { data, error } = await supabase
    .from('site_settings')
    .select('value')
    .eq('key', EXPENSE_BASELINE_KEY)
    .single();
  if (error || !data?.value) return {};
  const val = data.value as ExpenseBaselineMap;
  return typeof val === 'object' && val !== null ? { ...val } : {};
}

/** Per-month total (€) — if set, replaces base + legacy adjustment for that month. */
export async function fetchExpenseMonthOverrides(): Promise<ExpenseMonthOverridesMap> {
  const { data, error } = await supabase
    .from('site_settings')
    .select('value')
    .eq('key', EXPENSE_MONTH_OVERRIDES_KEY)
    .single();
  if (error || !data?.value) return {};
  const val = data.value as ExpenseMonthOverridesMap;
  return typeof val === 'object' && val !== null ? { ...val } : {};
}

export async function fetchExpenseAdjustments(): Promise<ExpenseAdjustmentsMap> {
  const { data, error } = await supabase
    .from('site_settings')
    .select('value')
    .eq('key', EXPENSE_ADJUSTMENTS_KEY)
    .single();
  if (error || !data?.value) return {};
  const val = data.value as Record<string, number>;
  return typeof val === 'object' && val !== null ? val : {};
}

export async function fetchExpenseBudgetState(): Promise<ExpenseBudgetState> {
  const [baseline, monthOverrides, adjustments] = await Promise.all([
    fetchExpenseBaselines(),
    fetchExpenseMonthOverrides(),
    fetchExpenseAdjustments(),
  ]);
  return { baseline, monthOverrides, adjustments };
}

/**
 * Baseline for YYYY-MM: latest segment with key not after ym, else the default.
 */
export function getBaselineExpensesForYearMonth(baseline: ExpenseBaselineMap, ym: string): number {
  const keys = Object.keys(baseline).filter((k) => k.length === 7 && k <= ym).sort();
  if (keys.length === 0) return DEFAULT_MONTHLY_EXPENSES;
  return Math.max(0, Number(baseline[keys[keys.length - 1]!]) || 0);
}

/**
 * Full rules: optional per-month total overrides baseline + optional legacy delta.
 */
export function getEffectiveMonthlyExpenses(
  year: number,
  month: number,
  state: ExpenseBudgetState
): number {
  const key = toYearMonthKey(year, month);
  const o = state.monthOverrides[key];
  if (o !== undefined && o !== null && !Number.isNaN(Number(o))) {
    return Math.max(0, Number(o));
  }
  const base = getBaselineExpensesForYearMonth(state.baseline, key);
  const adj = state.adjustments[key] ?? 0;
  return Math.max(0, base + adj);
}

/** @deprecated use getEffectiveMonthlyExpenses with fetchExpenseBudgetState */
export function getEffectiveExpenses(
  defaultExpenses: number,
  adjustments: ExpenseAdjustmentsMap,
  year: number,
  month: number
): number {
  const key = toYearMonthKey(year, month);
  const adj = adjustments[key] ?? 0;
  return Math.max(0, defaultExpenses + adj);
}

export async function setExpenseBaselineAtFirstOfMonth(
  year: number,
  month: number,
  amount: number
): Promise<void> {
  const map = await fetchExpenseBaselines();
  const key = toYearMonthKey(year, month);
  map[key] = Math.max(0, amount);
  const { error } = await supabase
    .from('site_settings')
    .upsert({ key: EXPENSE_BASELINE_KEY, value: map }, { onConflict: 'key' });
  if (error) throw error;
}

/** New baseline (€) starting on the 1st of the next calendar month in Europe/Athens. */
export async function setNewBaselineFromNextAthensMonth(amount: number): Promise<{ year: number; month: number }> {
  const n = getNextAthensYearMonth();
  await setExpenseBaselineAtFirstOfMonth(n.year, n.month, amount);
  return n;
}

export async function setExpenseAdjustment(year: number, month: number, adjustment: number): Promise<void> {
  const map = await fetchExpenseAdjustments();
  const key = toYearMonthKey(year, month);
  if (adjustment === 0) {
    delete map[key];
  } else {
    map[key] = adjustment;
  }
  const { error } = await supabase
    .from('site_settings')
    .upsert({ key: EXPENSE_ADJUSTMENTS_KEY, value: map }, { onConflict: 'key' });
  if (error) throw error;
}

/**
 * Store an absolute total for one month, or clear to fall back to baseline + legacy adj.
 * When setting a value, the legacy month adjustment for that YYYY-MM is cleared.
 */
export async function setExpenseMonthOverrideTotal(
  year: number,
  month: number,
  total: number | null
): Promise<void> {
  const key = toYearMonthKey(year, month);
  const [overrides, adjustments] = await Promise.all([fetchExpenseMonthOverrides(), fetchExpenseAdjustments()]);

  if (total === null) {
    delete overrides[key];
  } else {
    overrides[key] = Math.max(0, total);
    delete adjustments[key];
  }

  const w1 = supabase
    .from('site_settings')
    .upsert({ key: EXPENSE_MONTH_OVERRIDES_KEY, value: overrides }, { onConflict: 'key' });
  const w2 =
    total === null
      ? Promise.resolve({ error: null } as { error: Error | null })
      : supabase
          .from('site_settings')
          .upsert({ key: EXPENSE_ADJUSTMENTS_KEY, value: adjustments }, { onConflict: 'key' });

  const [r1, r2] = await Promise.all([w1, w2]);
  if (r1.error) throw r1.error;
  if (total !== null && r2.error) throw r2.error;
}
